import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {MutationObserver | null} */
  #tabsMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#setupTabsFromNav();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#tabsMutationObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Reads tabs from <product-tab-nav> and clones them into the sticky bar's tabs row(s).
   * There can be more than one tabs row in the DOM (desktop: inline in the bottom bar;
   * mobile: separate fixed bar pinned under the header) — both are kept in sync.
   * Keeps active-state in sync via MutationObserver (desktop only, see below).
   */
  #setupTabsFromNav() {
    const navEl = document.querySelector('product-tab-nav');
    if (!navEl) return;

    this.#populateTabsRow(navEl);

    // Sync active state when product-tab-nav changes its active tab (scroll highlight).
    // Applies on all breakpoints, including the mobile sticky tab bar: mirror the
    // active tab and scroll the active pill into view (like desktop).
    this.#tabsMutationObserver = new MutationObserver(() => {
      const activeTarget = navEl.querySelector('.pdp-tab--active')?.dataset.target;
      this.querySelectorAll('.sticky-add-to-cart__tabs-row').forEach((tabsRow) => {
        tabsRow.querySelectorAll('.pdp-tab').forEach((t) => {
          t.classList.toggle('pdp-tab--active', t.dataset.target === activeTarget);
        });
        const activeBtn = tabsRow.querySelector('.pdp-tab--active');
        if (activeBtn) this.#scrollTabIntoView(activeBtn);
      });
    });
    this.#tabsMutationObserver.observe(navEl, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  /**
   * Populates every `.sticky-add-to-cart__tabs-row` found in this component with
   * cloned buttons from product-tab-nav. Safe to call multiple times (clears
   * before filling). Tapping a tab updates the active state in ALL tabs rows
   * (kept in sync with each other) plus mirrors it into product-tab-nav.
   * @param {Element} [navEl]
   */
  #populateTabsRow(navEl) {
    const nav = navEl ?? document.querySelector('product-tab-nav');
    const tabsRows = this.querySelectorAll('.sticky-add-to-cart__tabs-row');
    if (!nav || !tabsRows.length) return;

    tabsRows.forEach((tabsRow) => {
      // Keep the scroll-to-top arrow (first child), remove only cloned tab buttons
      const scrollTopBtn = tabsRow.querySelector('.sticky-add-to-cart__scroll-top');
      tabsRow.innerHTML = '';
      if (scrollTopBtn) tabsRow.appendChild(scrollTopBtn);

      nav.querySelectorAll('.pdp-tab').forEach((srcTab) => {
        if (srcTab.hidden) return;
        const targetSection = document.querySelector(`[data-pdp-section="${srcTab.dataset.target}"]`);
        if (!targetSection) return;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = srcTab.className;
        btn.dataset.target = srcTab.dataset.target ?? '';
        btn.textContent = srcTab.textContent.trim();
        btn.addEventListener('click', () => {
          const target = btn.dataset.target;
          if (!target) return;
          const section = document.querySelector(`[data-pdp-section="${target}"]`);
          if (!section) return;

          // Scroll offset = height of product-tab-nav (sticky at top)
          const pdpNav = document.querySelector('product-tab-nav');
          const navHeight = pdpNav?.getBoundingClientRect().height ?? 0;
          const top = section.getBoundingClientRect().top + window.scrollY - navHeight - 8;
          window.scrollTo({ top, behavior: 'smooth' });

          // Update active state in every tabs row (desktop + mobile stay in sync)
          this.querySelectorAll('.sticky-add-to-cart__tabs-row').forEach((row) => {
            row.querySelectorAll('.pdp-tab').forEach((t) => {
              t.classList.toggle('pdp-tab--active', t.dataset.target === target);
            });
            const rowActiveBtn = row.querySelector('.pdp-tab--active');
            if (rowActiveBtn) this.#scrollTabIntoView(rowActiveBtn);
          });

          // Mirror active state in product-tab-nav
          nav.querySelectorAll('.pdp-tab').forEach((t) => {
            t.classList.toggle('pdp-tab--active', t.dataset.target === target);
          });
        });
        tabsRow.appendChild(btn);
      });
    });
  }

  /**
   * Scrolls a tab button into view within its own horizontal tabs row container.
   * @param {HTMLElement} tab
   */
  #scrollTabIntoView(tab) {
    const row = tab.closest('.sticky-add-to-cart__tabs-row');
    if (!row) return;
    const rowRect = row.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const scrollLeft = row.scrollLeft + (tabRect.left - rowRect.left) - 16;
    row.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    // If product-tab-nav exists, watch it instead of buy buttons —
    // show sticky bar only after the tab nav has scrolled out of view.
    const tabNav = document.querySelector('product-tab-nav');
    if (tabNav) {
      this.#buyButtonsIntersectionObserver.observe(tabNav);
    } else {
      this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    }
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (this.refs.addToCartButton.dataset.added === 'true') return;
    this.refs.addToCartButton.dataset.added = 'true';

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    await onAnimationEnd([this.refs.addToCartButton]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Re-populate tabs row (morph clears it from server response)
    this.#populateTabsRow();

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.querySelector('.sticky-add-to-cart__tabs-bar')?.setAttribute('data-stuck', 'true');
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.querySelector('.sticky-add-to-cart__tabs-bar')?.setAttribute('data-stuck', 'false');
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
